import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  CircleAlertIcon,
  CircleCheck,
  CircleQuestionMarkIcon,
  EditIcon,
  LinkIcon,
  PlusIcon,
} from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { TooltipTrigger } from "react-aria-components";
import z from "zod";

import { authClient } from "@/auth/client";
import { useViewer } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button, button } from "@/components/base/button";
import { DismissibleAlert } from "@/components/base/dismissible-alert";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { useAppForm } from "@/components/base/form";
import { Link } from "@/components/base/link";
import { Modal, ModalHeading } from "@/components/base/modal";
import { card, title } from "@/components/base/primitives";
import { addToast } from "@/components/base/toast";
import { Tooltip } from "@/components/base/tooltip";
import { AddProfileModal } from "@/components/profiles/add-profile-modal";
import { LinkWardModal } from "@/components/profiles/link-ward-modal";
import { ProfileListItem } from "@/components/profiles/list";
import { ProfileInvites } from "@/components/profiles/profile-invites";
import {
  ProfileRegistrations,
  PurchasedRegistrations,
} from "@/components/profiles/profile-registrations";
import { TshirtSizeEditor } from "@/components/tournaments/tshirt-size-editor";
import { VerifyPhoneForm } from "@/components/users/verify-phone-form";
import { updateUserMutationOptions } from "@/data/users";
import { getViewerProfilesQueryOptions } from "@/functions/profiles/get-viewer-profiles";
import { useNotLoggedInRedirect } from "@/hooks/auth";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { isUnauthorized } from "@/lib/errors";
import { normalizePhone, phoneValidationSchema } from "@/lib/phone";

export const Route = createFileRoute("/account/")({
  scripts: () => [
    {
      src: `${import.meta.env.VITE_USAEPAY_BASE_URL}/js/v2/pay.js`,
    },
  ],
  loader: async ({ context: { queryClient, trpc } }) => {
    try {
      const profiles = await queryClient.ensureQueryData(
        getViewerProfilesQueryOptions(),
      );

      // Send to setup only when the login manages no profiles at all. A
      // guardian-only account (manages kids, no self profile) is set up.
      if (profiles.length === 0) {
        throw redirect({ to: "/account/setup" });
      }

      // Prefetch registrations for each profile in parallel.
      // Use prefetchQuery so a failure doesn't block the page —
      // the component fetches via useQuery and handles loading/error.
      await Promise.all([
        ...profiles.map((p) =>
          queryClient.prefetchQuery(
            trpc.profiles.getRegistrations.queryOptions({ profileId: p.id }),
          ),
        ),
        // Registrations the viewer paid for but that aren't under their profiles.
        queryClient.prefetchQuery(
          trpc.profiles.getPurchasedRegistrations.queryOptions(),
        ),
      ]);
    } catch (err) {
      if (isUnauthorized(err)) {
        throw redirect({ to: "/log-in" });
      }

      throw err;
    }
  },
  component: RouteComponent,
});

// ── Split Profile Modal (Self-Service) ──

function SplitProfileModal({
  isOpen,
  onOpenChange,
  profileId,
  profileName,
  onSuccess,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  profileId: number;
  profileName: string;
  onSuccess: () => void;
}) {
  const trpc = useTRPC();

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.profiles.splitProfileToNewAccount.mutationOptions(),
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Account created",
        description: `Account created for ${data.email}. They'll need to reset their password to log in.`,
      });
      onOpenChange(false);
      onSuccess();
    },
  });

  const splitSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
  });

  const form = useAppForm({
    defaultValues: { email: "" },
    validators: { onMount: splitSchema, onChange: splitSchema },
    onSubmit: ({ value }) => {
      mutate({ profileId, email: value.email });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col gap-4 p-6">
        <ModalHeading size="xs">Create Separate Account</ModalHeading>
        <p className="text-sm text-gray-600">
          Enter an email address for <strong>{profileName}</strong>&apos;s new
          account. They&apos;ll receive a password reset email to claim it.
        </p>
        {failureReason && (
          <Alert
            color="error"
            title="Error"
            description={failureReason.message}
          />
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField
            name="email"
            children={(field) => (
              <field.Text
                field={field}
                label="Email"
                type="email"
                isRequired
                autoFocus
              />
            )}
          />
          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>
              <form.SubmitButton isDisabled={isPending}>
                {isPending ? "Creating..." : "Create Account"}
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}

function RouteComponent() {
  const viewer = useViewer();

  useNotLoggedInRedirect("/log-in");

  const { data: profiles } = useSuspenseQuery(getViewerProfilesQueryOptions());

  const hasGuardianProfile = profiles.some((p) => p.role === "guardian");

  const selfProfiles = profiles.filter((p) => p.role === "self");
  const hasMultipleSelf = selfProfiles.length > 1;

  const [splitTarget, setSplitTarget] = useState<{
    profileId: number;
    profileName: string;
  } | null>(null);

  const [verifyPhoneSent, setVerifyPhoneSent] = useState(false);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isLinkingProfile, setIsLinkingProfile] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [changeEmailMessage, setChangeEmailMessage] = useState<string | null>(
    null,
  );

  const { mutate: changeEmailMutate, failureReason: changeEmailError } =
    useMutation({
      mutationFn: async (email: string) => {
        const result = await authClient.changeEmail({
          newEmail: email,
          callbackURL: "/account",
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result;
      },
      onSuccess: () => {
        setIsChangingEmail(false);
        setChangeEmailMessage("Check your new email to verify the change.");
      },
    });

  const changeEmailSchema = z.object({
    newEmail: z.string().email("Please enter a valid email address"),
  });

  const changeEmailForm = useAppForm({
    defaultValues: {
      newEmail: "",
    },
    validators: {
      onMount: changeEmailSchema,
      onChange: changeEmailSchema,
    },
    onSubmit: ({ value }) => {
      changeEmailMutate(value.newEmail);
    },
  });

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate: sendOtp } = useMutation({
    mutationFn: async () => {
      if (viewer?.phoneNumber) {
        await authClient.phoneNumber.sendOtp({
          phoneNumber: viewer?.phoneNumber,
        });
      }
    },
    onSuccess: () => {
      setVerifyPhoneSent(true);

      queryClient.invalidateQueries(trpc.viewer.get.queryOptions());
    },
  });

  const phoneValidResult = phoneValidationSchema.safeParse(viewer?.phoneNumber);

  const { mutate: updateUser, failureReason } = useMutation({
    ...updateUserMutationOptions(),
    onSuccess: () => {
      setEdit(false);

      queryClient.invalidateQueries(trpc.viewer.get.queryOptions());
    },
  });

  const schema = z.object({
    name: z.string(),
    username: z.string().min(3, "Username must be at least 3 characters"),
    phoneNumber: phoneValidationSchema,
  });

  const form = useAppForm({
    defaultValues: {
      name: viewer?.name ?? "",
      username: viewer?.username ?? "",
      phoneNumber:
        viewer && phoneValidResult.success ? (viewer.phoneNumber ?? "") : "",
    },
    validators: {
      onMount: schema,
      onChange: schema,
      onChangeAsyncDebounceMs: 500,
      onChangeAsync: async ({ value }) => {
        // Only validate username if it changed
        if (!value.username || value.username === viewer?.username) {
          return undefined;
        }
        const result = await authClient.isUsernameAvailable({
          username: value.username,
        });
        if (!result.data?.available) {
          return {
            fields: {
              username: { message: "This username is already taken" },
            },
          };
        }
        return undefined;
      },
    },
    onSubmit: ({ value }) => {
      if (viewer) {
        updateUser({
          id: viewer.id,
          ...value,
          // biome-ignore lint/style/noNonNullAssertion: normalizePhone returns non-null for valid input
          phoneNumber: normalizePhone(value.phoneNumber)!,
        });
      }
    },
  });

  const [isEdit, setEdit] = useState(false);

  return (
    <DefaultLayout
      classNames={{
        content:
          "py-6 sm:py-12 w-full max-w-3xl mx-auto px-3 flex flex-col space-y-6 sm:space-y-12",
      }}
    >
      <Suspense>
        <h1
          className={title({
            size: "sm",
            class: "flex flex-row justify-between items-center",
          })}
        >
          <span>Account Information</span>
        </h1>

        <div
          className={card({
            class: "flex flex-col space-y-3 relative p-4 sm:p-6",
          })}
        >
          {!isEdit && !phoneValidResult.success && (
            <Alert
              color="warning"
              title="Phone number missing"
              description={
                <>
                  Please add a phone number to your account. A verified phone
                  number is needed for tournament communications.{" "}
                  <Button
                    variant="link"
                    color="primary"
                    size="sm"
                    onPress={() => setEdit(true)}
                  >
                    Update now
                  </Button>
                </>
              }
            />
          )}
          {isEdit ? (
            <form
              className="grid grid-cols-6 gap-3"
              onSubmit={(e) => {
                e.preventDefault();

                form.handleSubmit();
              }}
            >
              {failureReason && (
                <form.Alert
                  className="col-span-full"
                  color="error"
                  title="Uh oh!"
                  description={failureReason.message}
                />
              )}

              <form.AppField
                name="name"
                children={(field) => (
                  <field.Text
                    isRequired
                    className="col-span-full"
                    name="name"
                    label="Name"
                    field={field}
                    isDisabled={true}
                    description={
                      <p>
                        Contact{" "}
                        <Link to="mailto:info@cbva.com">info@cbva.com</Link> to
                        change this field.
                      </p>
                    }
                  />
                )}
              />

              <form.AppField
                name="username"
                children={(field) => (
                  <field.Text
                    className="col-span-full"
                    name="username"
                    label="Username"
                    field={field}
                  />
                )}
              />

              <form.AppField
                name="phoneNumber"
                children={(field) => (
                  <field.Text
                    isRequired
                    className="col-span-full"
                    name="phoneNumber"
                    label="Phone"
                    type="tel"
                    placeholder="+15555555555"
                    field={field}
                  />
                )}
              />

              <form.AppForm>
                <form.Footer className="col-span-full">
                  <Button onPress={() => setEdit(false)}>Cancel</Button>
                  <form.SubmitButton>Submit</form.SubmitButton>
                </form.Footer>
              </form.AppForm>
            </form>
          ) : (
            <>
              <Button
                size="sm"
                color="primary"
                className="absolute top-2 right-2 sm:top-3 sm:right-3"
                onPress={() => setEdit(true)}
              >
                <EditIcon size={12} className="-ml mr" />
                Update
              </Button>

              <div className="space-y flex flex-col">
                <span className="font-semibold">Name</span>

                <span>{viewer?.name}</span>
              </div>

              <div className="space-y flex flex-col">
                <span className="font-semibold">Username</span>

                <span>{viewer?.username ?? "Not set"}</span>
              </div>

              <div className="flex flex-col items-start">
                <span className="font-semibold">Email</span>

                {changeEmailMessage && (
                  <div className="mt-1 mb-2 flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
                    <CircleCheck
                      className="shrink-0 text-green-500"
                      size={16}
                    />
                    {changeEmailMessage}
                  </div>
                )}

                {!isChangingEmail ? (
                  <>
                    <span>{viewer?.email}</span>
                    <Button
                      className="pl-0"
                      color="primary"
                      variant="link"
                      size="sm"
                      onPress={() => {
                        setChangeEmailMessage(null);
                        setIsChangingEmail(true);
                      }}
                    >
                      Change Email
                    </Button>
                  </>
                ) : (
                  <form
                    className="mt-1 flex flex-col gap-2 self-stretch"
                    onSubmit={(e) => {
                      e.preventDefault();
                      changeEmailForm.handleSubmit();
                    }}
                  >
                    {changeEmailError && (
                      <changeEmailForm.Alert
                        color="error"
                        title="Error"
                        description={changeEmailError.message}
                      />
                    )}
                    <changeEmailForm.AppField
                      name="newEmail"
                      children={(field) => (
                        <field.Text
                          field={field}
                          label="New Email"
                          type="email"
                          isRequired
                          autoFocus
                        />
                      )}
                    />
                    <changeEmailForm.AppForm>
                      <changeEmailForm.Footer className="mt-0 justify-start">
                        <changeEmailForm.SubmitButton size="sm">
                          Submit
                        </changeEmailForm.SubmitButton>
                        <Button
                          size="sm"
                          onPress={() => {
                            setIsChangingEmail(false);
                            changeEmailForm.reset();
                          }}
                        >
                          Cancel
                        </Button>
                      </changeEmailForm.Footer>
                    </changeEmailForm.AppForm>
                  </form>
                )}
              </div>

              <div>
                <span className="font-semibold">Phone Number</span>

                {!verifyPhoneSent && (
                  <div className="flex flex-row items-center justify-between">
                    {phoneValidResult.success ? (
                      <>
                        <span>{viewer?.phoneNumber}</span>

                        {viewer?.phoneNumberVerified ? (
                          <div className="flex flex-row items-center space-x-1.5">
                            <CircleCheck className="text-green-500" size={16} />
                            <span className="text-xs">Verified</span>
                          </div>
                        ) : (
                          <Button
                            color="primary"
                            variant="link"
                            size="sm"
                            onPress={() => {
                              sendOtp();
                            }}
                          >
                            <CircleAlertIcon
                              className="text-red-500"
                              size={16}
                            />
                            Send verification code
                          </Button>
                        )}
                      </>
                    ) : (
                      <TooltipTrigger
                        delay={100}
                        closeDelay={50}
                        trigger="hover"
                      >
                        <Button
                          variant="text"
                          className="cursor-default no-underline"
                        >
                          Not Set{" "}
                          <CircleQuestionMarkIcon size={12} className="-ml" />
                        </Button>

                        <Tooltip
                          fill="fill-white"
                          className={card({
                            class: "max-w-xs p-3 border-none",
                          })}
                        >
                          <div className="space-y flex flex-col">
                            <p className="font-semibold">
                              If you had a phone number on your account before,
                              you either:
                            </p>

                            <ol className="list-decimal p-3">
                              <li>
                                Had multiple accounts with the same number and
                                we couldn't determine which was the main one.
                              </li>
                              <li>
                                Entered the number in a format that doesn't
                                satisfy the new system's validation.
                              </li>
                            </ol>
                          </div>
                        </Tooltip>
                      </TooltipTrigger>
                    )}
                  </div>
                )}

                {viewer?.phoneNumber && verifyPhoneSent && (
                  <VerifyPhoneForm
                    phoneNumber={viewer.phoneNumber}
                    onSuccess={() => {
                      queryClient.invalidateQueries(
                        trpc.viewer.get.queryOptions(),
                      );

                      setVerifyPhoneSent(false);
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Suspense>

      <Suspense>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-row items-center justify-between">
            <h2 className={title({ size: "sm" })}>
              Your profile{profiles && profiles.length > 1 ? "s" : null}
            </h2>

            <DropdownMenu
              label="Add profile"
              buttonClassName={button({ color: "primary", variant: "text" })}
              buttonContent={
                <>
                  <PlusIcon size={16} />
                  Add Profile
                </>
              }
              buttonIcon={false}
            >
              <DropdownMenuItem onPress={() => setIsCreatingProfile(true)}>
                <PlusIcon size={16} />
                Create New Profile
              </DropdownMenuItem>
              <DropdownMenuItem onPress={() => setIsLinkingProfile(true)}>
                <LinkIcon size={16} />
                Link Existing Player
              </DropdownMenuItem>
            </DropdownMenu>

            <LinkWardModal
              isOpen={isLinkingProfile}
              onOpenChange={setIsLinkingProfile}
            />

            <AddProfileModal
              isOpen={isCreatingProfile}
              onOpenChange={setIsCreatingProfile}
            />
          </div>

          {hasMultipleSelf && (
            <Alert
              color="info"
              title="Multiple profiles on this account"
              description="If one of these profiles belongs to someone else (e.g., a sibling), you can create a separate account for them using the button on their profile card."
            />
          )}

          {profiles.length > 0 && !hasGuardianProfile && (
            <DismissibleAlert
              dismissKey="child-registration-prompt"
              color="info"
              title="Need to register your child?"
              description={
                <>
                  Click <strong>Add Profile</strong> above to create or link
                  their profile, then head to tournament registration.
                </>
              }
            />
          )}

          {profiles && profiles.length > 0 ? (
            <div className="flex flex-col space-y-4">
              {profiles.map((profile) => (
                <div
                  key={profile.id}
                  className={card({ class: "flex flex-col p-4 sm:p-6" })}
                >
                  <ul>
                    <ProfileListItem
                      profile={profile}
                      linkNames
                      showActions
                      showPhotoUpload
                    />
                  </ul>
                  <ProfileInvites profileId={profile.id} />
                  <ProfileRegistrations profileId={profile.id} />
                  {profile.activeMembership && (
                    <>
                      <hr className="mx-2 mt-2 border-gray-200" />
                      <TshirtSizeEditor
                        membership={profile.activeMembership}
                        onUpdated={() =>
                          queryClient.invalidateQueries({
                            queryKey: ["viewer_profiles"],
                          })
                        }
                      />
                    </>
                  )}
                  {hasMultipleSelf && profile.role === "self" && (
                    <div className="mt-3 border-t border-gray-200 pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onPress={() =>
                          setSplitTarget({
                            profileId: profile.id,
                            profileName:
                              profile.preferredName ||
                              profile.firstName ||
                              "this player",
                          })
                        }
                      >
                        Create Separate Account
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={card({ class: "text-center py-8" })}>
              <p className="mb-4 text-gray-500">
                You haven't created a player profile yet.
              </p>
              <Link
                to="/account/setup"
                className="text-primary-600 font-medium hover:underline"
              >
                Create your profile
              </Link>
            </div>
          )}

          <PurchasedRegistrations />
        </div>
      </Suspense>

      {splitTarget && (
        <SplitProfileModal
          isOpen={!!splitTarget}
          onOpenChange={(open) => {
            if (!open) setSplitTarget(null);
          }}
          profileId={splitTarget.profileId}
          profileName={splitTarget.profileName}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["viewer_profiles"],
            });
          }}
        />
      )}
    </DefaultLayout>
  );
}
